import { Button } from '@progress/kendo-react-buttons';
import { Error } from '@progress/kendo-react-labels';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  GetFileExtensionsBySource,
  loadImageToBlobUrl
} from '../../utils/image-helper';
import { GameDocumentContext } from '../../contexts/game-document';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { uuid } from '../../types/common-helper';
import CameraModal from '../camera-modal';
import ImageViewWithFullScreen from '../image-view-with-fullscreen';
import { PlayerContext } from '../../contexts/player';
import { GetTaskById } from '../../utils/game-document/assets';
import { Loader } from '@progress/kendo-react-indicators';
import { getFromStore } from '../../utils/indexedDB';
import { TFileUpload } from '../../hooks/use-upload-chunk';
import { useFullscreen } from '../../hooks/use-fullscreen';
import { OverlayContext } from '../../contexts/overlay';
import { GetTaskContentById } from '../../utils/game-document/assets/task-contents';
import { useLocalStorage } from 'usehooks-ts';

const generateRandomString = () => {
  return uuid().split('-').join('').substring(0, 5);
};

const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

type Props = {
  isReadonly?: boolean;
  isUploading: boolean;
  selectedImage?: string;
  onSelectedImageCallback?: (blobUrl: string | File, size?: number) => void;
  isDisabled?: boolean;
  formId: string;
  taskId: string;
  isOnline: boolean;
  uploadStatus?: 'idle' | 'uploading' | 'failed' | 'complete';
  uploadError?: string | null;
  onRetry?: () => void;
  onCancel?: () => void;
};

const AnswerImage = ({
  isUploading,
  isReadonly = false,
  selectedImage = '',
  onSelectedImageCallback = () => {},
  isDisabled = false,
  formId,
  taskId,
  isOnline,
  uploadStatus = 'idle',
  uploadError = null,
  onRetry,
  onCancel
}: Props) => {
  const [player] = useContext(PlayerContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [overlay, setOverlay] = useContext(OverlayContext);

  const inputFile = useRef<HTMLInputElement>(null);
  const [blobImage, setBlobImage] = useState<string>(selectedImage);
  const [isValidFileExtension, setIsValidFileExtension] =
    useState<boolean>(true);
  const [imageKey, setImageKey] = useState<number>(0);
  const [resumeFullscreenAfterCamera, setResumeFullscreenAfterCamera] =
    useState(false);
  const { isFullscreen, exitFullscreen } = useFullscreen();
  const [cameraOpen, setCameraOpen, removeCameraOpen] = useLocalStorage(
    'camera-open',
    false
  );

  const playerCode = player.playerState?.code || '';

  useEffect(() => {
    setImageKey((prev) => prev + 1);
  }, [selectedImage]);

  const handleSelectImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const blobUrl = loadImageToBlobUrl(file);
      let taskName = '';
      const task = GetTaskById(gameDocument.gameDocument, taskId);
      if (task) {
        taskName = task.name;
      } else {
        const taskContent = GetTaskContentById(
          gameDocument.gameDocument,
          taskId
        );
        taskName = taskContent?.name ?? '';
      }
      const fileName = file.name;
      const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
      const newFileName = `${player!.playerState!.name}-${taskName}-${generateRandomString()}${fileExtension}`;

      setIsValidFileExtension(true);
      let extensionFound = undefined;

      if (fileExtension) {
        // set file ext checker
        let imageExtensionList = GetFileExtensionsBySource(
          'Assets - Task Content - Image Answer Type'
        );

        if (imageExtensionList) {
          extensionFound = imageExtensionList.find(
            (validFileExtension) =>
              validFileExtension ===
              fileExtension.replaceAll('.', '').toLowerCase()
          );
          setIsValidFileExtension(extensionFound ? true : false);
        }
      }

      if (extensionFound) {
        const newFile = new File([file], newFileName, { type: file.type });
        onSelectedImageCallback(newFile, newFile.size ?? 0);
        setBlobImage(blobUrl);
        setImageKey((prev) => prev + 1);
      } else {
        onSelectedImageCallback('');
        setBlobImage('');
      }
    }
  };

  const turnOnCameraClick = async () => {
    if (!overlay.isCameraPermissionGranted) {
      exitFullscreen();

      setOverlay((prev) => ({ ...prev, isCameraPermissionGranted: true }));
    }

    await new Promise((r) => setTimeout(r, 500));
    isFullscreen && setResumeFullscreenAfterCamera(isFullscreen);
    setCameraOpen(true);
    setIsValidFileExtension(true);
  };

  const handleCapture = useCallback(
    (screenShot: string) => {
      try {
        const imageBlob = dataURLtoBlob(screenShot);
        const extension = imageBlob.type.split('/')[1];

        let taskName = '';
        const task = GetTaskById(gameDocument.gameDocument, taskId);
        if (task) {
          taskName = task.name;
        } else {
          const taskContent = GetTaskContentById(
            gameDocument.gameDocument,
            taskId
          );
          taskName = taskContent?.name ?? '';
        }

        const file = new File(
          [imageBlob],
          `${player!.playerState!.name}-${taskName}-${generateRandomString()}.${extension}`,
          {
            type: imageBlob.type
          }
        );

        onSelectedImageCallback(file, imageBlob.size);
        setBlobImage(screenShot);
      } catch (error) {
        console.error(error);
      } finally {
        removeCameraOpen();
      }
    },
    [gameDocument.gameDocument, taskId, onSelectedImageCallback]
  );

  const onAddImageClick = () => inputFile.current && inputFile.current.click();

  useEffect(() => {
    if (!playerCode) return;

    (async () => {
      const files = await getFromStore<TFileUpload>('upload-queue', playerCode);

      if (files) {
        const selectedFile = files.files.find(
          (f) => f.taskId === taskId && f.formId === formId
        );

        if (selectedFile) {
          // Use stored local image for visual preview only
          if (selectedFile.base64Data && !blobImage) {
            setBlobImage(selectedFile.base64Data);
          }

          // Only pass final remote URL to answer callback
          if (selectedFile.status === 'complete' && selectedFile.url) {
            onSelectedImageCallback(selectedFile.url);
          }
        }
      }
    })();
  }, [playerCode, taskId, formId, isUploading, isOnline]);

  const displayImage = selectedImage || blobImage;

  return (
    <div id={'answer-image'} className={'d-flex flex-column flex-grow-1 h-100'}>
      <div
        className={
          'd-flex flex-column justify-content-center border text-center position-relative'
        }
        style={{
          flex: 1,
          overflow: 'hidden'
        }}>
        <div
          className={`${isUploading ? 'd-flex' : 'd-none'} flex-column justify-content-center align-items-center h-100 p-3`}>
          <Loader type={'infinite-spinner'} size={'large'} />
          <span className="mt-2">
            {generateTitleById(
              '71a2dffb-7ae7-422f-8423-93b0d4f15a4a',
              gameDocument,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Uploading...'}
          </span>
          {onCancel && (
            <Button
              className="mt-3 btn-sm"
              fillMode="outline"
              themeColor="base"
              onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>

        {!isOnline && uploadStatus === 'failed' ? (
          <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 text-center">
            <span className="material-symbols-outlined text-primary text-size-64 mb-2">
              cloud_off
            </span>
            <span className="text-primary fw-bold text-size-18 mb-1">
              Offline - Photo saved!
            </span>
            <span className="text-muted small">
              Your photo is safely stored on this device. Upload will automatically resume once you're back online.
            </span>
          </div>
        ) : uploadStatus === 'failed' && !isUploading ? (
          <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 text-center">
            <span className="material-symbols-outlined text-danger text-size-48 mb-2">
              error
            </span>
            <span className="text-danger fw-bold mb-1">
              {uploadError ||
                'Upload failed. Please check your connection and retry.'}
            </span>
            <span className="text-muted small mb-3">
              Your photo is preserved. You can retry upload or choose a new
              photo.
            </span>
            {onRetry && (
              <Button
                themeColor="primary"
                className="px-3 py-2"
                onClick={onRetry}>
                <span className="material-symbols-outlined mr-2">refresh</span>
                Retry Upload
              </Button>
            )}
          </div>
        ) : null}

        <div
          className={`${isUploading || uploadStatus === 'failed' ? 'd-none' : 'd-flex'} justify-content-center align-items-center h-100`}>
          {!displayImage ? (
            <div
              className={
                'd-flex flex-column align-items-center justify-content-center'
              }>
              <span
                className={
                  'material-symbols-outlined text-success text-size-64'
                }>
                wallpaper
              </span>
              <span>
                {generateTitleById(
                  '567963b7-47c0-40e9-8659-2e43a5a5befb',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Preview'}
              </span>
            </div>
          ) : (
            <ImageViewWithFullScreen
              key={imageKey}
              src={displayImage}
              alt={'preview'}>
              <div className="image-preview-container">
                <img
                  src={displayImage}
                  alt={'Preview'}
                  className="preview-image"
                />
              </div>
            </ImageViewWithFullScreen>
          )}
        </div>
      </div>

      {/* currently commented as requested from User Story #5365 */}
      {/* {isLargeImageSize && (
        <Row>
          <Error>Please reduce your file size below 500 Kb</Error>
        </Row>
      )} */}
      {!isReadonly && (
        <Row className={'py-2'}>
          {!isValidFileExtension && (
            <Col className={'mb-4'} md={12}>
              <Error>
                {generateTitleById(
                  '4af50ef9-db25-48cf-9fa2-2a5ad295d6cf',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Invalid File Type: Please upload image files only.'}
              </Error>
            </Col>
          )}
          {!isDisabled && (
            <div className={'d-flex flex-column gap-2'}>
              <Button
                className={'w-100 upload-button p-2'}
                disabled={isUploading}
                onClick={turnOnCameraClick}>
                <div
                  className={
                    'd-flex justify-content-center align-items-center gap-3'
                  }>
                  <span
                    className={'material-symbols-outlined mr-2 text-success'}
                    role={'button'}>
                    add_a_photo
                  </span>
                  <span>
                    {generateTitleById(
                      '7b63892e-236c-4b6a-bfec-d5d3d66794af',
                      gameDocument,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Take photo'}
                  </span>
                </div>
              </Button>
              <Button
                className={'w-100 upload-button p-2'}
                disabled={isUploading}
                onClick={onAddImageClick}>
                <div
                  className={
                    'd-flex justify-content-center align-items-center gap-3'
                  }>
                  <span
                    className={'material-symbols-outlined mr-2 text-success'}
                    role={'button'}>
                    add_photo_alternate
                  </span>
                  <span>
                    {generateTitleById(
                      'b6d7d97b-37bd-4ef9-aa9e-0e02a404a375',
                      gameDocument,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Add from files'}
                  </span>
                </div>
              </Button>
              <input
                type={'file'}
                id={'answer-file-upload'}
                className={'d-none'}
                onChange={handleSelectImage}
                onClick={(event) => {
                  event.currentTarget.value = '';
                }}
                ref={inputFile}
                accept={'image/*'}
              />
            </div>
          )}
        </Row>
      )}

      <CameraModal
        isOpen={cameraOpen}
        isResumeFullscreenAfterCamera={resumeFullscreenAfterCamera}
        onClose={removeCameraOpen}
        onCapture={handleCapture}
      />
    </div>
  );
};

export default AnswerImage;
